import React from 'react';
import { FormField } from '../../../components';
import { useAuth } from '../../../context/AuthContext';

/**
 * Finance step of the resident wizard.
 * The form branches on the residence type:
 *   - CARE_HOME   → weekly-fee based (agreed/assessed weekly fee + dependency level)
 *   - DOMICILIARY → hourly based (hourly rate × agreed weekly hours = estimated fee)
 * Shared sections: funding setup, finance contact, payment & notes.
 */

const FUNDING_TYPES = [
    { label: 'Private', value: 'Private' },
    { label: 'Local Authority', value: 'Local Authority' },
    { label: 'NHS', value: 'NHS' },
    { label: 'Mixed / Third-party top-up', value: 'Mixed' },
];

const REFERRAL_SOURCES = [
    { label: 'Social Services', value: 'Social Services' },
    { label: 'GP', value: 'GP' },
    { label: 'Hospital', value: 'Hospital' },
    { label: 'Family', value: 'Family' },
    { label: 'Self', value: 'Self' },
    { label: 'Other', value: 'Other' },
];

const CAREHOME_CARE_TYPES = [
    { label: 'Residential Care', value: 'Residential Care' },
    { label: 'Nursing Care', value: 'Nursing Care' },
    { label: 'Dementia Care', value: 'Dementia Care' },
    { label: 'Respite Care', value: 'Respite Care' },
];

const DOMICILIARY_CARE_TYPES = [
    { label: 'Personal Care', value: 'Personal Care' },
    { label: 'Domestic Support', value: 'Domestic Support' },
    { label: 'Companionship', value: 'Companionship' },
    { label: 'Live-in Care', value: 'Live-in Care' },
    { label: 'Respite Care', value: 'Respite Care' },
];

const FEE_FREQUENCIES = [
    { label: 'Weekly', value: 'Weekly' },
    { label: 'Fortnightly', value: 'Fortnightly' },
    { label: 'Monthly', value: 'Monthly' },
];

const DEPENDENCY_LEVELS = [
    { label: 'Low', value: 'Low' },
    { label: 'Standard', value: 'Standard' },
    { label: 'High', value: 'High' },
    { label: 'Complex', value: 'Complex' },
];

const RELATIONSHIPS = [
    { label: 'Wife', value: 'Wife' },
    { label: 'Husband', value: 'Husband' },
    { label: 'Son', value: 'Son' },
    { label: 'Daughter', value: 'Daughter' },
    { label: 'Solicitor', value: 'Solicitor' },
    { label: 'Self', value: 'Self' },
    { label: 'Other', value: 'Other' },
];

const YES_NO = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
];

const PAYMENT_METHODS = [
    { label: 'Direct debit', value: 'Direct debit' },
    { label: 'Bank transfer', value: 'Bank transfer' },
    { label: 'Standing order', value: 'Standing order' },
    { label: 'Cheque', value: 'Cheque' },
    { label: 'Invoice', value: 'Invoice' },
];

const PAYMENT_STATUSES = [
    { label: 'Pending', value: 'Pending' },
    { label: 'Active', value: 'Active' },
    { label: 'Overdue', value: 'Overdue' },
    { label: 'Paid', value: 'Paid' },
];

const SectionTitle = ({ children }) => (
    <h3 className="text-[14px] font-bold text-[#1E293B] dark:text-gray-100 mb-4">{children}</h3>
);

// Step navigation lives in the ConvertClient page header, top-right.
const FinanceDetails = ({ updateData, data, residenceType }) => {
    const { user } = useAuth();
    const finance = data.financeDetails || {};
    // The domiciliary variant must follow the logged-in organization first
    // (a domiciliary org always uses hourly-rate billing), then fall back to
    // the per-residence type. Without this, a missing/unset residence_type
    // defaulted to CARE_HOME and showed the wrong Fee-details labels.
    const orgType = (user?.organization?.organization_type || '').toUpperCase();
    const recordType = (residenceType || data.residence_type || '').toUpperCase();
    const isDomiciliary = orgType === 'DOMICILIARY' || recordType === 'DOMICILIARY';

    const handleChange = (e) => {
        const { name, value } = e.target;
        updateData('financeDetails', { [name]: value });
    };

    // Domiciliary estimated weekly fee = hourly rate × weekly hours (kept in agreed_amount).
    const handleRateOrHoursChange = (e) => {
        const { name, value } = e.target;
        const next = { ...finance, [name]: value };
        const rate = parseFloat(next.hourly_rate);
        const hours = parseFloat(next.care_hours_required);
        const patch = { [name]: value };
        if (!Number.isNaN(rate) && !Number.isNaN(hours)) {
            patch.agreed_amount = (rate * hours).toFixed(2);
        }
        updateData('financeDetails', patch);
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row gap-10">

                {/* Left Column */}
                <div className="flex-1 flex flex-col gap-8">

                    {/* Column header — matches the client design */}
                    <h2 className="text-[18px] font-bold text-[#1E293B] dark:text-gray-100">
                        {isDomiciliary ? 'Domiciliary Finance Flow' : 'Care Home Finance Flow'}
                    </h2>

                    {/* 1. Funding setup */}
                    <div>
                        <SectionTitle>1. Funding setup</SectionTitle>
                        <div className="grid grid-cols-2 gap-5">
                            <FormField
                                label="Type of funding"
                                name="funding_type"
                                type="select"
                                value={finance.funding_type || ''}
                                onChange={handleChange}
                                placeholder="Select funding type"
                                options={FUNDING_TYPES}
                                required
                            />
                            <FormField
                                label="Funding body"
                                name="funding_body"
                                value={finance.funding_body || ''}
                                onChange={handleChange}
                                placeholder="e.g. Essex County Council"
                            />
                            <FormField
                                label="Source of referral"
                                name="source_of_referral"
                                type="select"
                                value={finance.source_of_referral || ''}
                                onChange={handleChange}
                                placeholder="Select source"
                                options={REFERRAL_SOURCES}
                            />
                            <FormField
                                label="Type of care required"
                                name="care_type_required"
                                type="select"
                                value={finance.care_type_required || ''}
                                onChange={handleChange}
                                placeholder="Select care type"
                                options={isDomiciliary ? DOMICILIARY_CARE_TYPES : CAREHOME_CARE_TYPES}
                            />
                        </div>
                    </div>

                    {/* 2. Fee details */}
                    <div>
                        <SectionTitle>2. Fee details</SectionTitle>
                        <div className="flex flex-col gap-5">
                            <FormField
                                label={isDomiciliary ? 'Service start date' : 'Admission date'}
                                name="admission_date"
                                type="date"
                                value={finance.admission_date ? String(finance.admission_date).split('T')[0] : ''}
                                onChange={handleChange}
                                required
                            />

                            <div className="grid grid-cols-2 gap-5">
                                <FormField
                                    label="Billing start date"
                                    name="billing_start_date"
                                    type="date"
                                    value={finance.billing_start_date ? String(finance.billing_start_date).split('T')[0] : ''}
                                    onChange={handleChange}
                                    required
                                />
                                <FormField
                                    label="Fee frequency"
                                    name="fee_frequency"
                                    type="select"
                                    value={finance.fee_frequency || ''}
                                    onChange={handleChange}
                                    placeholder="Select frequency"
                                    options={FEE_FREQUENCIES}
                                />
                            </div>

                            {isDomiciliary ? (
                                <>
                                    <div className="grid grid-cols-2 gap-5">
                                        <FormField
                                            label="Hourly rate (£)"
                                            name="hourly_rate"
                                            type="number"
                                            value={finance.hourly_rate || ''}
                                            onChange={handleRateOrHoursChange}
                                            placeholder="0.00"
                                        />
                                        <FormField
                                            label="Agreed weekly hours"
                                            name="care_hours_required"
                                            type="number"
                                            value={finance.care_hours_required || ''}
                                            onChange={handleRateOrHoursChange}
                                            placeholder="e.g. 24"
                                        />
                                    </div>
                                    <FormField
                                        label="Estimated weekly fee (£)"
                                        name="agreed_amount"
                                        type="number"
                                        value={finance.agreed_amount || ''}
                                        onChange={handleChange}
                                        placeholder="0.00"
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-5">
                                        <FormField
                                            label="Agreed weekly fee (£)"
                                            name="agreed_amount"
                                            type="number"
                                            value={finance.agreed_amount || ''}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                        />
                                        <FormField
                                            label="Assessed weekly fee (£)"
                                            name="assessed_fee"
                                            type="number"
                                            value={finance.assessed_fee || ''}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <FormField
                                        label="Care dependency level"
                                        name="care_dependency_level"
                                        type="select"
                                        value={finance.care_dependency_level || ''}
                                        onChange={handleChange}
                                        placeholder="Select level"
                                        options={DEPENDENCY_LEVELS}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Vertical Dashed Divider */}
                <div className="hidden md:block w-px border-l-2 border-dashed border-gray-300 dark:border-gray-700"></div>

                {/* Right Column */}
                <div className="flex-1 flex flex-col gap-8">

                    {/* Column header — matches the client design */}
                    <h2 className="text-[18px] font-bold text-[#1E293B] dark:text-gray-100">
                        Invoice, Payment &amp; Notes
                    </h2>

                    {/* 3. Finance contact */}
                    <div>
                        <SectionTitle>3. Finance contact</SectionTitle>
                        <div className="grid grid-cols-2 gap-5">
                            <FormField
                                label="Invoice contact name"
                                name="invoice_contact_name"
                                value={finance.invoice_contact_name || ''}
                                onChange={handleChange}
                                placeholder="e.g. Jane Doe"
                            />
                            <FormField
                                label="Relationship"
                                name="invoice_contact_relationship"
                                type="select"
                                value={finance.invoice_contact_relationship || ''}
                                onChange={handleChange}
                                placeholder="Select relationship"
                                options={RELATIONSHIPS}
                            />
                            <FormField
                                label="Invoice email"
                                name="invoice_email"
                                type="email"
                                value={finance.invoice_email || ''}
                                onChange={handleChange}
                                placeholder="name@example.com"
                            />
                            <FormField
                                label="Invoice phone"
                                name="invoice_phone"
                                value={finance.invoice_phone || ''}
                                onChange={handleChange}
                                placeholder="07700 900456"
                            />
                            <FormField
                                label="LPA holder for finance"
                                name="lpa_holder_finance"
                                type="select"
                                value={finance.lpa_holder_finance || ''}
                                onChange={handleChange}
                                placeholder="Select"
                                options={YES_NO}
                            />
                            <FormField
                                label="PO / reference"
                                name="po_reference"
                                value={finance.po_reference || ''}
                                onChange={handleChange}
                                placeholder="Optional"
                            />
                        </div>
                    </div>

                    {/* 4. Payment and notes */}
                    <div>
                        <SectionTitle>4. Payment and notes</SectionTitle>
                        <div className="flex flex-col gap-5">
                            <div className="grid grid-cols-2 gap-5">
                                <FormField
                                    label="Payment method"
                                    name="payment_method"
                                    type="select"
                                    value={finance.payment_method || ''}
                                    onChange={handleChange}
                                    placeholder="Select method"
                                    options={PAYMENT_METHODS}
                                />
                                <FormField
                                    label="Payment status"
                                    name="payment_status"
                                    type="select"
                                    value={finance.payment_status || ''}
                                    onChange={handleChange}
                                    placeholder="Select status"
                                    options={PAYMENT_STATUSES}
                                />
                            </div>
                            <FormField
                                label="Funding notes"
                                name="income_note"
                                type="textarea"
                                rows={3}
                                value={finance.income_note || ''}
                                onChange={handleChange}
                                placeholder="Any additional notes about funding / payment"
                            />
                        </div>
                    </div>

                    {/* Summary card */}
                    <div className="rounded-xl border border-[#E5EAF3] dark:border-gray-700 bg-[#F8FAFC] dark:bg-[#31373f] p-5">
                        <h4 className="text-[14px] font-bold text-[#1E293B] dark:text-gray-100 mb-4">
                            {isDomiciliary ? 'Domiciliary summary' : 'Care home summary'}
                        </h4>
                        <div className="divide-y divide-[#E5EAF3] dark:divide-gray-700">
                            <div className="flex items-center justify-between py-2.5">
                                <span className="text-[13px] text-[#64748B] dark:text-gray-400">Billing basis</span>
                                <span className="text-[13px] font-bold text-[#1E293B] dark:text-gray-100">
                                    {isDomiciliary ? 'Hourly rate' : 'Weekly fee'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between py-2.5">
                                <span className="text-[13px] text-[#64748B] dark:text-gray-400">Shown only here</span>
                                <span className="text-[13px] font-bold text-[#1E293B] dark:text-gray-100">
                                    {isDomiciliary ? 'Service start date' : 'Admission date'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between py-2.5">
                                <span className="text-[13px] text-[#64748B] dark:text-gray-400">Care field</span>
                                <span className="text-[13px] font-bold text-[#1E293B] dark:text-gray-100">
                                    {isDomiciliary ? 'Weekly hours' : 'Dependency level'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default FinanceDetails;
